import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'
import BreedsData from "../data/breeds";

Vue.use(Vuex)

export const vuexPersist = new VuexPersist({
	key: 'mla-fdc',
	storage: window.localStorage
})

// COMMON

export type ChartTypes = 'Bar' | 'Line' | 'Radar'

export interface Header {
	text: string;
	value: string;
	align: string;
	class?: string;
}

export interface Dataset {
	label: string;
	data: number[];
	type?: string;
	backgroundColor?: string | CanvasPattern;
	borderColor?: string | CanvasPattern;
	borderWidth?: number | object;
	fill?: boolean;
	_meta?: any;
	yAxisID?: string;
	lineTension?: number;
	order?: number;
	hidden?: boolean;
	pointStyle?: string;
	pointRadius?: number;
  pointBorderColor?: string;
	stack?: string;
}

// PASTURE

export const EnterpriseTypes = ['Cattle & Sheep', 'Cattle', 'Sheep'] as const;

export type PastureKeys = 'p1' | 'p2';
export type EnterpriseType = typeof EnterpriseTypes[number];
export type GrowthRateSources = 'Add Pasture Types' | 'User-supplied Average Values' | 'Standard Average Values'

export interface PasturePair {
	type: string | null;
	name: PastureKeys;
	label?: string | null;
	scale: number
}

interface MonthlyPastureRoot {
	supplement: {
		amount: number;
		quality: number;
	};
	month: string;
	avgGrowth: number;
	avgQuality: number;
	total: number;
}

export type MonthlyPasture = MonthlyPastureRoot & {
	[key in PastureKeys]: {
		area: number;
		growth: number;
		pasture: PasturePair;
		quality: number;
	};
};

export type Supply = {
	[key: string]: {
		ha: number;
		pasture: string;
		tonnesDM: number[];
		tonnesDMPerHa: number[];
		carryOver: number[];
		total: number;
		seasons: {} | null;
	}
} | null

export interface PastureSimulation {
	date: string | Date;
	pastures: PasturePair[];
	growthRateSource: GrowthRateSources;
	location: number;
	enterprise: EnterpriseType | null;
	name: string;
	area: number;
	areaIsEditable: boolean;
	growingWastage: number;
	carryoverWastage: number;
	soil: string | null;
	slope: string | null;
	defaultScale: number;
	customPastureQuality: 'No';
}

export interface Pasture {
	monthlyPasture: MonthlyPasture[];
	monthlyPastureHeaders: Header[];
	simulation: PastureSimulation;
}

export interface PastureModel {
	ID: number;
	LocationID: number;
	Pasture: string;
	Weight: number;
	Legume: number;
	Months: number[];
}

// STOCK

export type BreedNamesCattle = typeof BreedsData.cattle.names[number];
export type BreedNamesSheep = typeof BreedsData.sheep.names[number];
export type BreedNames = BreedNamesCattle | BreedNamesSheep;
export type StockNamesCattle = typeof BreedsData.cattle.classes.breeding[number];
export type StockNamesSheep = typeof BreedsData.sheep.classes.breeding[number];
export type StockNames = StockNamesCattle | StockNamesSheep;
export type StockType = 'Breeding' | 'Trading';

export interface Stock {
	class: StockNames;
	purchaseAge: number;
	purchaseDate: string;
	purchaseNumber: number;
	purchaseWeight: number;
	defaultPurchaseWeight?: number | null;
	saleAge: number;
	saleDate: string;
	saleNumber: number;
	saleAvailable?: number | null;
	saleWeight: number;
	defaultSaleWeight?: number | null;
	startAge: number;
	startNumber: number | null;
	startWeight: number | null;
	defaultStartWeight?: number | null;
	type: StockType | '';
}

export interface Livestock {
	breed: BreedNames;
	weight: number;
	joinOption: true;
	joinNumber: number;
	joinPeriods: {
		fromDate: string | Date;
		toDate: string | Date;
		percentage: number;
		weanDate: string | Date;
		expectation: number;
	}[]
}

export interface Enterprise {
	calculations: any;
	livestock: Livestock;
	stockHeaders: Header[];
	stock: Stock[];
	chartType: ChartTypes;
}

// FEED

export interface KPI {
	id: string;
	indicator: string;
	value: number;
	unit: string;
	desc: string;
}

// STORE

export interface Store {
	[key: string]: Enterprise | any;
	resetData: boolean;
	elearning: boolean;
	resetDisable: boolean;
	errors: {
		pasture: string[];
		livestock: string[];
	}
	rules: {
		required: any; // (value:any) => !!value || value === 0 || 'Required.',
		notNegative: any; // (value:number) => value >= 0 || 'No negative values.'
	}
	pasture: Pasture | null;
	cattle: Enterprise;
	sheep: Enterprise;
	types: any;
	supply: {
		[key: string]: {
			ha: number;
			pasture: string;
			tonnesDM: number[];
			tonnesDMPerHa: number[];
			carryOver: number[];
			total: number;
		}
	} | null;
	demand: any;
	calculations: {
		cattle: any,
		sheep: any
	}
}

const getDefaultState = () => {
	return {
		resetData: false,
		elearning: false,
		resetDisable: false,
		errors: {
			pasture: [],
			livestock: []
		},
		errorAlert: false,
		rules: {
			required: (value: any) => !!value || value > 0 || 'Required.',
			notNegative: (value: number) => value >= 0 || 'No negative values.'
		},
		pasture: null,
		cattle: null!,
		sheep: null!,
		types: null,
		supply: null,
		demand: null,
		calculations: {
			cattle: null,
			sheep: null
		}
	}
}

const state = getDefaultState()

export default new Vuex.Store<Store>({
	state: state,
	mutations: {
		resetState (state: any, clearLocalStorage) {
      if (!clearLocalStorage) {
				localStorage.removeItem('mla-fdc');
			}
      Object.assign(state, getDefaultState())
    },
		resetDisable(state: any, disable) {
			state.resetDisable = disable
		},
		pasture(state, updatedState) {
			state.pasture = updatedState
		},
		cattle(state, updatedState) {
			state.cattle = updatedState
		},
		sheep(state, updatedState) {
			state.sheep = updatedState
		},
		calculations(state, updatedState) {
			state.calculations = updatedState
		}
	},
	actions: {
    resetState ({ commit }, clearLocalStorage) {
      commit('resetState', clearLocalStorage)
    },
		resetDisable({ commit }, disable) {
			commit('resetDisable', disable)
		},
		updatePasture({ commit }, updatedState) {
			commit('pasture', updatedState)
		},
		updateCattle({ commit }, updatedState) {
			commit('cattle', updatedState)
		},
		updateSheep({ commit }, updatedState) {
			commit('sheep', updatedState)
		},
		updateCalculations({ commit }, updatedState) {
			commit('calculations', updatedState)
		}
	},
	getters: {
		sheep: state => {
			return state.sheep
		},
		cattle: state => {
			return state.cattle
		},
		calculations: (state) => {
			return state.calculations
		}
	},
	plugins: [vuexPersist.plugin]
})
